import { useEffect } from "react";
import { trackEvent } from "../../lib/telemetry";

const SETTLE_MS = 800;
const MIN_LENGTH = 3;
const MAX_TERM_LENGTH = 100;

// Module scope, so both last for the visit and nothing is kept on the device.
const reported = new Set<string>();
let held: string | null = null;

function flush() {
  if (held && !reported.has(held)) {
    reported.add(held);
    trackEvent("HelpSearchNoResults", { term: held });
  }
  held = null;
}

// A zero-result term is held rather than sent, because a later term that extends it means the
// customer was still typing and the shorter one was never a real search.
function settle(term: string, hitCount: number) {
  if (held && !term.startsWith(held)) flush();
  held = hitCount === 0 ? term : null;
}

/** Reports help searches that settled on no results, once per term per visit. */
export function useSearchGapReporting(query: string, hitCount: number) {
  const term = query.trim().toLowerCase().slice(0, MAX_TERM_LENGTH);

  useEffect(() => {
    if (term.length < MIN_LENGTH) return;
    const timer = setTimeout(() => settle(term, hitCount), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [term, hitCount]);

  useEffect(() => {
    // Hidden rather than pagehide: closing the tab never unmounts, and the SDK flushes its own
    // queue on pagehide, after this has had its turn.
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      flush();
    };
  }, []);
}
